package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Drive
import com.dotdrives.model.DrivePhase
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.NewDrive
import com.dotdrives.model.NewGiver
import com.dotdrives.repository.DriveRepository
import com.dotdrives.repository.GiverRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

data class NewDriveUiState(
    val giverQuery: String = "",
    val giverResults: List<GiverSummary> = emptyList(),
    val searchingGivers: Boolean = false,
    val selectedGiver: GiverSummary? = null,
    val targetId: String? = null,
    val askAmount: String = "",
    val templateId: String? = null,
    val initialPhaseId: String? = null,
    val firstPhaseName: String? = null,
    val firstPhaseTaskCount: Int = 0,
    val stewardUserId: String? = null,
    val saving: Boolean = false,
    val savedDrive: Drive? = null,
    val error: String? = null,
)

class NewDriveController(
    private val giverRepo: GiverRepository,
    private val driveRepo: DriveRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val defaultStewardUserId: String? = null,
    private val defaultTemplateId: String? = null,
) : DotController<NewDriveUiState>(
    NewDriveUiState(templateId = defaultTemplateId, stewardUserId = defaultStewardUserId),
    scope,
) {
    private var searchJob: Job? = null
    private var generation = 0

    fun start(templateId: String? = defaultTemplateId, initialPhaseId: String? = null) {
        generation++
        searchJob?.cancel()
        update { NewDriveUiState(templateId = templateId, initialPhaseId = initialPhaseId, stewardUserId = defaultStewardUserId) }
        templateId?.let(::loadPhasePreview)
    }

    fun searchGiver(query: String) {
        val request = generation
        searchJob?.cancel()
        update { it.copy(giverQuery = query, error = null) }
        val trimmed = query.trim()
        if (trimmed.isEmpty()) {
            update { it.copy(giverResults = emptyList(), searchingGivers = false) }
            return
        }
        searchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MILLIS)
            if (request != generation) return@launch
            update { it.copy(searchingGivers = true) }
            giverRepo.list(orgId, trimmed, null, null, null, 1, GIVER_RESULTS_LIMIT)
                .onSuccess { result ->
                    if (request != generation) return@onSuccess
                    update { it.copy(giverResults = result.items, searchingGivers = false) }
                }
                .onFailure { error ->
                    if (request != generation) return@onFailure
                    update { it.copy(searchingGivers = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    fun selectGiver(giver: GiverSummary) {
        update {
            it.copy(
                selectedGiver = giver,
                giverQuery = giver.displayName,
                giverResults = emptyList(),
                error = null,
            )
        }
    }

    fun createGiverInline(displayName: String) {
        val request = generation
        val trimmed = displayName.trim()
        if (trimmed.isBlank()) return
        update { it.copy(saving = true, error = null) }
        scope.launch {
            giverRepo.create(
                orgId,
                NewGiver(
                    giverType = "individual",
                    firstName = "",
                    lastName = "",
                    displayName = trimmed,
                    spouseName = null,
                    email = null,
                    phone = null,
                    address = null,
                    stewardUserId = null,
                    tags = emptyList(),
                ),
            ).onSuccess { giver ->
                if (request != generation) return@onSuccess
                update {
                    it.copy(
                        saving = false,
                        selectedGiver = GiverSummary(
                            id = giver.id,
                            displayName = giver.displayName,
                            giverType = giver.giverType,
                            stewardUserId = giver.stewardUserId,
                            stewardName = null,
                            temperature = giver.temperature,
                            tags = giver.tags,
                        ),
                        giverQuery = giver.displayName,
                        giverResults = emptyList(),
                    )
                }
            }.onFailure { error ->
                if (request != generation) return@onFailure
                update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
            }
        }
    }

    fun setTargetId(id: String?) {
        update { it.copy(targetId = id, error = null) }
    }

    fun setAskAmount(text: String) {
        update { it.copy(askAmount = text, error = null) }
    }

    fun setTemplateId(id: String) {
        update { it.copy(templateId = id, initialPhaseId = null, error = null, firstPhaseName = null, firstPhaseTaskCount = 0) }
        loadPhasePreview(id)
    }

    private fun loadPhasePreview(templateId: String) {
        val request = generation
        val initialPhaseId = state.value.initialPhaseId
        scope.launch {
            val phases = driveRepo.phases(orgId, templateId).getOrElse { emptyList() }
            val firstPhase = if (initialPhaseId != null) phases.firstOrNull { it.id == initialPhaseId }
                else phases.minWithOrNull(compareBy<DrivePhase> { it.sequence }.thenBy { it.id })
            if (firstPhase == null) return@launch
            if (request != generation || state.value.templateId != templateId || state.value.initialPhaseId != initialPhaseId) return@launch
            val taskCount = driveRepo.phaseTasks(orgId, firstPhase.id).getOrElse { emptyList() }.size
            if (request != generation || state.value.templateId != templateId || state.value.initialPhaseId != initialPhaseId) return@launch
            update { it.copy(firstPhaseName = firstPhase.name, firstPhaseTaskCount = taskCount) }
        }
    }

    fun setStewardUserId(id: String) {
        update { it.copy(stewardUserId = id, error = null) }
    }

    fun save() {
        val request = generation
        val current = state.value
        if (current.saving) return
        val giver = current.selectedGiver
        if (giver == null) {
            update { it.copy(error = "Choose a giver to start this drive") }
            return
        }
        val templateId = current.templateId
        if (templateId == null) {
            update { it.copy(error = "Choose a process template") }
            return
        }
        val stewardUserId = current.stewardUserId
        if (stewardUserId == null) {
            update { it.copy(error = "Choose a steward") }
            return
        }
        val askAmount = current.askAmount.trim().takeIf(String::isNotEmpty)?.toDoubleOrNull()
        val newDrive = NewDrive(
            title = giver.displayName,
            giverId = giver.id,
            advocateGiverId = null,
            stewardUserId = stewardUserId,
            templateId = templateId,
            targetId = current.targetId,
            targetText = null,
            askAmount = askAmount,
            initialPhaseId = current.initialPhaseId,
        )
        update { it.copy(saving = true, error = null, savedDrive = null) }
        scope.launch {
            driveRepo.create(orgId, newDrive)
                .onSuccess { saved ->
                    if (request == generation) update { it.copy(saving = false, savedDrive = saved, error = null) }
                }
                .onFailure { error ->
                    if (request == generation) update { it.copy(saving = false, error = error.message ?: "Something went wrong") }
                }
        }
    }
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
private const val GIVER_RESULTS_LIMIT = 6
